import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { appSupportService } from "@/features/app-support/services/appSupportService";
import {
  getPresignedUrl,
  uploadToS3Direct,
  confirmAttachmentInDB,
  deleteAttachmentFromAll,
} from "@/shared/services/uploadService";
import { getMimeType } from "@/shared/utils/fileHelpers";
import { formatDateTime } from "@/shared/utils/formatters";
import { Button } from "@/shared/components/ui/Button";
import { Badge } from "@/shared/components/ui/Badge";
import { Toast } from "@/shared/components/feedback/Toast";
import { AttachmentDropZone } from "@/features/announcements/components/AttachmentDropZone";
import { AttachmentList } from "@/features/announcements/components/AttachmentList";
import { CommentSection } from "@/features/announcements/components/CommentSection";
import type { AttachmentUploadItem } from "@/features/announcements/types/feed";
import type {
  AppSupportTicket,
  AppSupportStatus,
  AppSupportSeverity,
  AppSupportCategory,
  AppSupportUpdateData,
} from "@/features/app-support/types/appSupport";
import {
  STATUS_OPTIONS,
  SEVERITY_OPTIONS,
  CATEGORY_OPTIONS,
  STATUS_LABELS,
  SEVERITY_LABELS,
  CATEGORY_LABELS,
} from "@/features/app-support/types/appSupport";

const ACCEPTED_MIME_TYPES = ["application/pdf", "image/jpeg", "image/png", "image/gif", "image/webp"];
const MAX_FILE_SIZE = 10 * 1024 * 1024;

type TabId = "info" | "comments" | "files";

const statusVariant: Record<AppSupportStatus, "default" | "success" | "warning" | "danger" | "info"> = {
  NEW:             "info",
  TRIAGED:         "default",
  IN_PROGRESS:     "warning",
  WAITING_ON_USER: "warning",
  RESOLVED:        "success",
  CLOSED:          "success",
};

const severityVariant: Record<AppSupportSeverity, "default" | "success" | "warning" | "danger" | "info"> = {
  LOW:    "default",
  NORMAL: "info",
  HIGH:   "warning",
  URGENT: "danger",
};

interface AppSupportDetailPanelProps {
  ticket: AppSupportTicket | null;
  isOpen: boolean;
  onClose: () => void;
  onUpdated: (updated: AppSupportTicket) => void;
}

export const AppSupportDetailPanel = ({
  ticket,
  isOpen,
  onClose,
  onUpdated,
}: AppSupportDetailPanelProps) => {
  const { t } = useTranslation();
  const { userData } = useAuth();
  const isPlatformAdmin = userData?.is_platform_admin ?? false;

  const [activeTab, setActiveTab] = useState<TabId>("info");
  const [editForm, setEditForm] = useState<AppSupportUpdateData>({});
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: "success" | "error" } | null>(null);
  const [attachments, setAttachments] = useState<AppSupportTicket["attachments"]>([]);
  const [uploadQueue, setUploadQueue] = useState<AttachmentUploadItem[]>([]);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (ticket) {
      setEditForm({
        status:      ticket.status,
        assigned_to: ticket.assigned_to ?? null,
        resolution:  ticket.resolution ?? "",
        severity:    ticket.severity,
        category:    ticket.category ?? undefined,
      });
      setAttachments(ticket.attachments ?? []);
    }
  }, [ticket]);

  const showToast = (message: string, type: "success" | "error" = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleSave = async () => {
    if (!ticket) return;
    setSaving(true);
    try {
      const updated = await appSupportService.updateTicket(ticket.ticket_id, editForm);
      onUpdated(updated);
      showToast(t("common.success"));
    } catch {
      showToast(t("common.error"), "error");
    } finally {
      setSaving(false);
    }
  };

  const handleFilesSelected = (files: File[]) => {
    const items: AttachmentUploadItem[] = files
      .filter((f) => {
        const mime = getMimeType(f.name);
        return ACCEPTED_MIME_TYPES.includes(f.type || mime) && f.size <= MAX_FILE_SIZE;
      })
      .map((f) => ({
        localId: `${Date.now()}-${Math.random()}`,
        file: f,
        progress: 0,
        status: "pending" as const,
      }));
    setUploadQueue((prev) => [...prev, ...items]);
  };

  const handleUploadAll = async () => {
    if (!ticket || uploadQueue.length === 0) return;
    setUploading(true);
    for (let i = 0; i < uploadQueue.length; i++) {
      const item = uploadQueue[i];
      if (item.status !== "pending") continue;
      try {
        setUploadQueue((prev) =>
          prev.map((q, idx) => idx === i ? { ...q, status: "uploading" } : q),
        );
        const fileName = item.file.name;
        const fileType = item.file.type || getMimeType(fileName);
        const { uploadUrl, s3Key, bucketName } = await getPresignedUrl(
          fileName, fileType, "app_support", ticket.ticket_id,
        );
        await uploadToS3Direct(uploadUrl, item.file, fileType, (progress) => {
          setUploadQueue((prev) =>
            prev.map((q, idx) => idx === i ? { ...q, progress } : q),
          );
        });
        const confirmed = await confirmAttachmentInDB({
          s3_key: s3Key,
          s3_bucket: bucketName,
          display_name: fileName,
          relation_type: "app_support",
          relation_id: String(ticket.ticket_id),
          file_type: fileType,
        });
        setAttachments((prev) => [...(prev ?? []), {
          attachment_id: confirmed.attachment_id,
          s3_key: s3Key,
          s3_bucket: bucketName,
          name: fileName,
          type: fileType,
        }]);
        setUploadQueue((prev) =>
          prev.map((q, idx) => idx === i ? { ...q, status: "done", progress: 100 } : q),
        );
      } catch (err) {
        console.error("[AppSupport upload error]", err);
        showToast(t("common.error"), "error");
        setUploadQueue((prev) =>
          prev.map((q, idx) => idx === i ? { ...q, status: "error" } : q),
        );
      }
    }
    setUploading(false);
    setUploadQueue((prev) => prev.filter((q) => q.status !== "done"));
  };

  const handleDeleteAttachment = async (attachmentId: string | number) => {
    const id = String(attachmentId);
    try {
      await deleteAttachmentFromAll(id);
      setAttachments((prev) => (prev ?? []).filter((a) => a.attachment_id !== id));
      showToast(t("common.success"));
    } catch {
      showToast(t("common.error"), "error");
    }
  };

  if (!isOpen || !ticket) return null;

  const tabs: { id: TabId; label: string }[] = [
    { id: "info",     label: t("inquiry.tabs.info") },
    { id: "comments", label: t("inquiry.tabs.comments") },
    { id: "files",    label: t("inquiry.tabs.attachments") },
  ];

  return (
    <>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/20 z-30" onClick={onClose} />

      {/* Panel */}
      <div className="fixed right-0 top-0 h-full w-full max-w-2xl bg-white shadow-2xl z-40 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3 min-w-0">
            <span className="text-sm font-mono text-gray-400">SR#{ticket.ticket_id}</span>
            <Badge variant={statusVariant[ticket.status]}>
              {t(STATUS_LABELS[ticket.status])}
            </Badge>
            <Badge variant={severityVariant[ticket.severity]}>
              {t(SEVERITY_LABELS[ticket.severity])}
            </Badge>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 ml-2 flex-shrink-0">
            <X size={20} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 px-6">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? "border-indigo-600 text-indigo-600"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* ── Info Tab ── */}
          {activeTab === "info" && (
            <div className="space-y-5">
              <div>
                <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">{t("app_support.title_label")}</p>
                <p className="text-base font-semibold text-gray-900">{ticket.title}</p>
              </div>

              {ticket.description && (
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">{t("app_support.description")}</p>
                  <p className="text-sm text-gray-700 whitespace-pre-line">{ticket.description}</p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">{t("app_support.category")}</p>
                  <p className="text-sm text-gray-700">
                    {ticket.category ? t(CATEGORY_LABELS[ticket.category]) : "—"}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">{t("app_support.severity")}</p>
                  <Badge variant={severityVariant[ticket.severity]}>
                    {t(SEVERITY_LABELS[ticket.severity])}
                  </Badge>
                </div>
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">{t("app_support.status")}</p>
                  <Badge variant={statusVariant[ticket.status]}>
                    {t(STATUS_LABELS[ticket.status])}
                  </Badge>
                </div>
              </div>

              {ticket.browser_info && (
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">{t("app_support.browser_info")}</p>
                  <p className="text-xs text-gray-500 break-all">{ticket.browser_info}</p>
                </div>
              )}

              {ticket.resolution && (
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">{t("app_support.resolution")}</p>
                  <p className="text-sm text-gray-700 whitespace-pre-line">{ticket.resolution}</p>
                </div>
              )}

              {/* Support/admin edit controls */}
              {isPlatformAdmin && (
                <div className="border-t border-gray-100 pt-4 space-y-4">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    {t("app_support.manage")}
                  </p>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">{t("app_support.status")}</label>
                      <select
                        value={editForm.status ?? ticket.status}
                        onChange={(e) => setEditForm((p) => ({ ...p, status: e.target.value as AppSupportStatus }))}
                        className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      >
                        {STATUS_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>{t(o.label)}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs text-gray-500 mb-1">{t("app_support.severity")}</label>
                      <select
                        value={editForm.severity ?? ticket.severity}
                        onChange={(e) => setEditForm((p) => ({ ...p, severity: e.target.value as AppSupportSeverity }))}
                        className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      >
                        {SEVERITY_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>{t(o.label)}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs text-gray-500 mb-1">{t("app_support.category")}</label>
                      <select
                        value={editForm.category ?? ticket.category ?? ""}
                        onChange={(e) => setEditForm((p) => ({ ...p, category: e.target.value as AppSupportCategory || null }))}
                        className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      >
                        <option value="">{t("common.select")}</option>
                        {CATEGORY_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>{t(o.label)}</option>
                        ))}
                      </select>
                    </div>

                  </div>

                  <div>
                    <label className="block text-xs text-gray-500 mb-1">{t("app_support.resolution")}</label>
                    <textarea
                      value={editForm.resolution ?? ticket.resolution ?? ""}
                      onChange={(e) => setEditForm((p) => ({ ...p, resolution: e.target.value }))}
                      rows={3}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-y"
                    />
                  </div>

                  <div className="flex justify-end">
                    <Button onClick={handleSave} disabled={saving}>
                      {saving ? t("common.loading") : t("common.save")}
                    </Button>
                  </div>
                </div>
              )}

              {/* Footer metadata */}
              <div className="border-t border-gray-100 pt-4 grid grid-cols-2 gap-3 text-xs text-gray-400">
                <div>
                  <span className="block font-medium">{t("inquiry.footer.created_by")}</span>
                  <span>{ticket.created_by_name ?? "—"}</span>
                </div>
                <div>
                  <span className="block font-medium">{t("app_support.reporter_email")}</span>
                  <span>{ticket.created_by_email ?? "—"}</span>
                </div>
                <div>
                  <span className="block font-medium">{t("app_support.business_unit")}</span>
                  <span className="font-mono">{ticket.business_unit}</span>
                </div>
                <div>
                  <span className="block font-medium">{t("inquiry.footer.last_updated")}</span>
                  <span>{ticket.updated_at ? formatDateTime(ticket.updated_at) : "—"}</span>
                </div>
                {ticket.closed_at && (
                  <div>
                    <span className="block font-medium">{t("app_support.closed_at")}</span>
                    <span>{formatDateTime(ticket.closed_at)}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── Comments Tab ── */}
          {activeTab === "comments" && (
            <CommentSection
              announcementId={ticket.ticket_id}
              relationType="app_support"
              currentUserId={userData?.id ?? ""}
              isOfficer={isPlatformAdmin}
            />
          )}

          {/* ── Files Tab ── */}
          {activeTab === "files" && (
            <div className="space-y-4">
              <AttachmentDropZone
                onFilesSelected={handleFilesSelected}
                accept={ACCEPTED_MIME_TYPES.join(",")}
                disabled={uploading}
              />
              {uploadQueue.length > 0 && (
                <div className="space-y-2">
                  {uploadQueue.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-sm bg-gray-50 rounded-lg px-3 py-2">
                      <span className="flex-1 truncate text-gray-700">{item.file.name}</span>
                      {item.status === "uploading" && (
                        <span className="text-indigo-500 text-xs">{item.progress}%</span>
                      )}
                      {item.status === "done" && <span className="text-green-500 text-xs">✓</span>}
                      {item.status === "error" && <span className="text-red-500 text-xs">!</span>}
                      {item.status === "pending" && (
                        <button
                          onClick={() => setUploadQueue((prev) => prev.filter((_, i) => i !== idx))}
                          className="text-gray-400 hover:text-red-500 text-lg leading-none"
                        >×</button>
                      )}
                    </div>
                  ))}
                  {uploadQueue.some((q) => q.status === "pending") && (
                    <div className="flex justify-end">
                      <Button onClick={handleUploadAll} disabled={uploading}>
                        {uploading ? t("common.loading") : t("app_support.upload_files")}
                      </Button>
                    </div>
                  )}
                </div>
              )}
              <AttachmentList
                attachments={(attachments ?? []).map((a) => ({
                  attachment_id: a.attachment_id,
                  relation_type: "app_support",
                  relation_id:   String(ticket.ticket_id),
                  s3_key:        a.s3_key,
                  s3_bucket:     a.s3_bucket,
                  display_name:  a.name,
                  file_type:     a.type,
                }))}
                uploads={[]}
                onDelete={handleDeleteAttachment}
                onRename={() => {}}
                onCancelUpload={() => {}}
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
};
